import { getMetadataSectionList } from './product-metadata.util';

export type MicroNutritionRow = {
  nutritionId: string;
  groupName: string;
  nutrition: string;
  measurement?: string;
  uomId?: string | null;
  defaultValue: number | null;
  rdaValue: number | null;
  recommendedDailyValue: number | null;
};

export type MacroNutritionRow = {
  standardMacroKey: string;
  label: string;
  /** FssaiNutrientMasters id — required for reliable save (label-only resolve often fails). */
  nutrientId?: string | null;
  unitId: string | number | null;
  amount: number | null;
  percentDv: number | null;
  rda: number | null;
};

export const STANDARD_MACRO_LABELS = [
  'Energy',
  'Protein',
  'Total Fat',
  'Saturated Fat',
  'Trans Fat',
  'Carbohydrates',
  'Dietary Fiber',
  'Total Sugars',
  'Added Sugars',
  'Cholesterol',
  'Sodium',
  'Calcium',
] as const;

export function mapMicroNutritionParamsFromMetadata(items: any[]): MicroNutritionRow[] {
  const list = getMetadataSectionList({ microNutritionParams: items }, [
    'microNutritionParams',
    'MicroNutritionParams',
  ]);

  return (list || [])
    .map((item: any) => ({
      nutritionId: String(item.id ?? item.Id ?? ''),
      groupName: String(item.description ?? item.Description ?? item.groupName ?? 'Others'),
      nutrition: String(item.name ?? item.Name ?? item.fieldName ?? ''),
      measurement: item.label ?? item.Label ?? item.measurement ?? undefined,
      uomId: item.parentId ?? item.ParentId ?? null,
      defaultValue: null,
      rdaValue: null,
      recommendedDailyValue: null,
    }))
    .filter((row) => !!row.nutritionId && !!row.nutrition);
}

export function filterMicroNutritionRows(
  rows: MicroNutritionRow[],
  searchTerm: string
): MicroNutritionRow[] {
  const query = searchTerm.trim().toLowerCase();
  if (!query) {
    return rows;
  }
  return rows.filter(
    (row) =>
      row.groupName.toLowerCase().includes(query) ||
      row.nutrition.toLowerCase().includes(query)
  );
}

export function createMacroNutritionRow(label: string): MacroNutritionRow {
  return {
    standardMacroKey: label,
    label,
    nutrientId: null,
    unitId: null,
    amount: null,
    percentDv: null,
    rda: null,
  };
}

export function getAvailableStandardMacroOptions(
  macronutrientList: MacroNutritionRow[]
): Array<{ key: string; label: string }> {
  const added = new Set(macronutrientList.map((row) => row.standardMacroKey.toLowerCase()));
  return STANDARD_MACRO_LABELS.filter((label) => !added.has(label.toLowerCase())).map((label) => ({
    key: label,
    label,
  }));
}

/**
 * Map a free-text nutrient name to one of the standard label keys.
 * More specific fat/sugar variants are matched before generic Total Fat / Total Sugars.
 */
export function resolveStandardMacroLabel(
  name: string,
  labels: readonly string[] = STANDARD_MACRO_LABELS
): string {
  const n = (name || '').toLowerCase().trim();
  if (!n) {
    return '';
  }

  const exact = labels.find((label) => label.toLowerCase() === n);
  if (exact) {
    return exact;
  }

  const has = (key: string) => labels.some((label) => label.toLowerCase() === key);
  const pick = (key: string) => labels.find((label) => label.toLowerCase() === key) || '';

  if (has('trans fat') && n.includes('trans') && n.includes('fat')) {
    return pick('trans fat');
  }
  if (has('saturated fat') && n.includes('satur') && n.includes('fat')) {
    return pick('saturated fat');
  }
  // Combined "Total Sugar (Natural + Added)" must map to Total Sugars, not Added Sugars.
  if (
    has('total sugars') &&
    (n.includes('total sugar') ||
      (n.includes('natural sugar') && n.includes('added')) ||
      (n.includes('sugar') && !n.includes('added')))
  ) {
    return pick('total sugars');
  }
  if (has('added sugars') && n.includes('added') && n.includes('sugar') && !n.includes('total sugar')) {
    return pick('added sugars');
  }
  if (has('total fat') && (n === 'fat' || n.includes('total fat') || n.includes('fat')) && !n.includes('satur') && !n.includes('trans')) {
    return pick('total fat');
  }
  if (has('carbohydrates') && (n.includes('carb') || n === 'carbohydrate')) {
    return pick('carbohydrates');
  }
  if (has('dietary fiber') && (n.includes('fiber') || n.includes('fibre'))) {
    return pick('dietary fiber');
  }
  if (has('energy') && (n.includes('energy') || n.includes('calorie'))) {
    return pick('energy');
  }
  if (has('protein') && n.includes('protein')) {
    return pick('protein');
  }
  if (has('cholesterol') && n.includes('cholesterol')) {
    return pick('cholesterol');
  }
  if (has('sodium') && (n.includes('sodium') || n === 'salt')) {
    return pick('sodium');
  }
  if (has('calcium') && n.includes('calcium')) {
    return pick('calcium');
  }

  return '';
}

/** Whether a master nutrient name matches a selected standard label (for ID resolution). */
export function masterNameMatchesStandardMacroLabel(masterName: string, standardLabel: string): boolean {
  const name = (masterName || '').toLowerCase().trim();
  const normalized = (standardLabel || '').toLowerCase().trim();
  if (!name || !normalized) {
    return false;
  }
  if (name === normalized) {
    return true;
  }

  if (normalized === 'trans fat') {
    return name.includes('trans') && name.includes('fat');
  }
  if (normalized === 'saturated fat') {
    return name.includes('satur') && name.includes('fat');
  }
  if (normalized === 'total fat') {
    return (
      name === 'fat' ||
      name.includes('total fat') ||
      (name.includes('fat') && !name.includes('satur') && !name.includes('trans'))
    );
  }
  if (normalized === 'added sugars') {
    // Do not map the combined "Total Sugar (Natural + Added)" master to Added Sugars.
    return (
      name.includes('added') &&
      name.includes('sugar') &&
      !name.includes('total sugar') &&
      !name.includes('natural sugar')
    );
  }
  if (normalized === 'total sugars') {
    return (
      name.includes('total sugar') ||
      (name.includes('natural sugar') && name.includes('added')) ||
      (name.includes('sugar') && !name.includes('added'))
    );
  }
  if (normalized === 'carbohydrates') {
    return name.includes('carb') || name === 'carbohydrate';
  }
  if (normalized === 'dietary fiber') {
    return name.includes('fiber') || name.includes('fibre');
  }
  if (normalized === 'energy') {
    return name.includes('energy') || name.includes('calorie');
  }
  if (normalized === 'protein') {
    return name.includes('protein');
  }
  if (normalized === 'cholesterol') {
    return name.includes('cholesterol');
  }
  if (normalized === 'sodium') {
    return name.includes('sodium') || name === 'salt';
  }

  return name.includes(normalized);
}
